import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  MessageCircle,
  Trash2,
  Tag as TagIcon,
  Flag,
  StickyNote,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

export default function ConversationsPanel({
  lang,
  activeId,
  onSelect,
  refreshTrigger,
}) {
  const t = useT(lang);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [newCountry, setNewCountry] = useState("");
  const [newTags, setNewTags] = useState("");

  const [notesDrafts, setNotesDrafts] = useState({}); // id -> notes text

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/conversations");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const create = async () => {
    if (!newLabel.trim()) {
      toast.error(t("convLabelRequired"));
      return;
    }
    try {
      const tags = newTags
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const { data } = await api.post("/conversations", {
        label: newLabel.trim(),
        country: newCountry.trim() || null,
        notes: "",
        tags,
      });
      setNewLabel("");
      setNewCountry("");
      setNewTags("");
      setItems((prev) => [data, ...prev]);
      onSelect(data.id);
      toast.success(t("convCreated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/conversations/${id}`);
      setItems((prev) => prev.filter((c) => c.id !== id));
      if (activeId === id) onSelect(null);
      toast.success(t("convDeleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    }
  };

  const saveNotes = async (id) => {
    const notes = notesDrafts[id];
    if (notes === undefined) return;
    try {
      const { data } = await api.patch(`/conversations/${id}`, { notes });
      setItems((prev) => prev.map((c) => (c.id === id ? data : c)));
      setNotesDrafts((d) => {
        const { [id]: _drop, ...rest } = d;
        return rest;
      });
      toast.success(t("convNotesSaved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    }
  };

  return (
    <div className="surface p-6 md:p-7" data-testid="conversations-panel">
      <div className="flex items-center gap-2 mb-1">
        <MessageCircle size={14} strokeWidth={1.5} className="text-[#FF006E]" />
        <span className="overline">{t("convOverline")}</span>
      </div>
      <h2 className="font-display text-lg font-semibold mb-5">
        {t("convTitle")}
      </h2>

      {/* Create form */}
      <div className="surface-inner p-4 mb-5">
        <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2 block">
          {t("convNewLabel")}
        </Label>
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder={t("convNewLabelPh")}
          className="field-input bg-transparent mb-3"
          data-testid="new-conv-label-input"
        />
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Input
            value={newCountry}
            onChange={(e) => setNewCountry(e.target.value)}
            placeholder={t("convCountryPh")}
            className="field-input bg-transparent"
            data-testid="new-conv-country-input"
          />
          <Input
            value={newTags}
            onChange={(e) => setNewTags(e.target.value)}
            placeholder={t("convTagsPh")}
            className="field-input bg-transparent"
            data-testid="new-conv-tags-input"
          />
        </div>
        <button
          type="button"
          onClick={create}
          className="cta-primary rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] inline-flex items-center gap-2"
          data-testid="create-conv-btn"
        >
          <Plus size={14} strokeWidth={1.8} />
          {t("convCreateBtn")}
        </button>
      </div>

      {/* List */}
      <div className="space-y-3" data-testid="conv-list">
        {loading ? (
          <p className="text-zinc-500 text-sm">{t("convLoading")}</p>
        ) : items.length === 0 ? (
          <p className="text-zinc-500 text-sm" data-testid="conv-empty">
            {t("convEmpty")}
          </p>
        ) : (
          items.map((c) => {
            const selected = c.id === activeId;
            const notesValue =
              notesDrafts[c.id] !== undefined ? notesDrafts[c.id] : c.notes || "";
            return (
              <div
                key={c.id}
                className={`surface-inner p-4 transition-colors cursor-pointer ${
                  selected
                    ? "border-[#FF006E]/50"
                    : "hover:border-white/20"
                }`}
                onClick={() => onSelect(c.id)}
                data-testid={`conv-item-${c.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-sm" data-testid={`conv-label-${c.id}`}>
                      {c.label}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {c.country && (
                        <span className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1">
                          <Flag size={10} strokeWidth={1.6} />
                          {c.country}
                        </span>
                      )}
                      {(c.tags || []).map((tg) => (
                        <span
                          key={tg}
                          className="text-[10px] uppercase tracking-widest text-[#FF006E]/80 border border-[#FF006E]/30 rounded-full px-2 py-[1px] inline-flex items-center gap-1"
                        >
                          <TagIcon size={9} strokeWidth={1.6} />
                          {tg}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-zinc-500 hover:text-[#FF006E] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(c.id);
                    }}
                    data-testid={`delete-conv-${c.id}`}
                    aria-label="delete"
                  >
                    <Trash2 size={15} strokeWidth={1.6} />
                  </button>
                </div>

                {selected && (
                  <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1 flex items-center gap-1">
                      <StickyNote size={11} strokeWidth={1.5} />
                      {t("convNotes")}
                    </Label>
                    <Textarea
                      value={notesValue}
                      onChange={(e) =>
                        setNotesDrafts((d) => ({ ...d, [c.id]: e.target.value }))
                      }
                      onBlur={() => saveNotes(c.id)}
                      placeholder={t("convNotesPh")}
                      className="field-textarea bg-transparent min-h-[70px] text-[13px]"
                      data-testid={`conv-notes-${c.id}`}
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
